/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import { MAX_FILE_SIZE, MAX_MB } from "backend/src/common/constants/asset";
import { AttachmentIcon, DeleteCircleIcon, SendIcon } from "icons";
import React, { ChangeEvent, useState } from "react";
import { TextArea, Button, FileTrigger } from "react-aria-components";
import { Spinner, Text } from "ui-components";

import { useAuth } from "@/hooks/useAuth";
import { makeToast } from "@/src/components/Toast/Toast";
import {
  MessageAttachment,
  MessageAttachmentFileType,
  useMessageSendMessage,
} from "@/src/hooks/api/generated";
import { useChat } from "@/src/hooks/useChat";
import { useChatFunctions } from "@/src/hooks/useChatFunctions";
import { useUpload } from "@/src/hooks/useUpload";
import { concatString } from "@/src/utils/string";

export function MessageComposer(): JSX.Element {
  const [text, setText] = useState("");
  const [numberOfRow, setNumberOfRow] = useState(1);
  const [messageAttachments, setMessageAttachments] = useState<
    MessageAttachment[]
  >([]);
  const [files, setFiles] = useState<File | undefined>();

  const { selectedConversation, conversations } = useChat();
  const { setSelectedConversationByConversationData, appendConversationToTop } =
    useChatFunctions();

  const { authData, setAuthData } = useAuth();
  const isBlocked = selectedConversation?.isBlocked;

  const { mutate: sendMessage } = useMessageSendMessage();
  const { mutate: uploadAttachments, status: uploadAttachmentsStatus } =
    useUpload();

  function handleUploadFile(fileForUpload: File): void {
    uploadAttachments(
      {
        data: {
          file: fileForUpload,
        },
      },
      {
        onSuccess: (data) => {
          setMessageAttachments([
            {
              fileType: fileForUpload.type as MessageAttachmentFileType,
              original_name: fileForUpload.name,
              name: data,
            },
          ]);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          setFiles(undefined);
          makeToast({
            title: error.message,
            intent: "danger",
          });
        },
      }
    );
  }

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const newText = event.target.value;
    setText(newText);
  };

  const handleSendPress = (): void => {
    sendMessage(
      {
        data: {
          content: text,
          recipientIds: selectedConversation?.recipientIds,
          conversationId: selectedConversation?.conversationId,
          attachments: messageAttachments,
        },
      },
      {
        onSuccess(newMessages) {
          const isConversationAlreadyExists = conversations?.data.find(
            (con) => con.id === newMessages.conversation.id
          );
          if (
            !selectedConversation?.conversationId ||
            !isConversationAlreadyExists
          ) {
            setSelectedConversationByConversationData(newMessages.conversation);
            appendConversationToTop(newMessages);
          }
        },
        onError: (error: any) => {
          if (
            error.statusCode === 422 &&
            error.message === "Spam message detected" &&
            authData
          ) {
            const updatedAuthData = {
              ...authData,
              is_chat_blocked: true,
            };
            setAuthData?.({
              data: updatedAuthData,
              isAuthenticated: true,
            });
          }
        },
      }
    );
    setText("");
    setFiles(undefined);
    setMessageAttachments([]);
  };
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault(); // Prevents the default action of entering a new line
      if (text.length < 30) {
        handleSendPress();
      } else {
        setNumberOfRow((num) => num + 1);
      }
    }
  };

  return (
    <div className="flex flex-col border-t p-5 ">
      <div className="flex flex-1 flex-grow  gap-5  items-center ">
        <TextArea
          disabled={isBlocked}
          value={text}
          rows={numberOfRow}
          placeholder={
            isBlocked
              ? "You can't send message to this conversation."
              : "Type your message here"
          }
          className=" flex-1 resize-none outline-none max-h-[100px] data-[disabled]:bg-white"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />

        {files ? (
          <div className="flex gap-1 justify-center items-center">
            {uploadAttachmentsStatus === "pending" && <Spinner />}
            <Text>{concatString(files.name, 20)}</Text>
            <Button onPress={() => setFiles(undefined)}>
              <DeleteCircleIcon />
            </Button>
          </div>
        ) : (
          <FileTrigger
            acceptedFileTypes={Object.keys(MessageAttachmentFileType)}
            onSelect={(e) => {
              if (e) {
                const [newFile] = Array.from(e);
                if (newFile) {
                  if (newFile.size > MAX_FILE_SIZE) {
                    makeToast({
                      intent: "danger",
                      title: `File too large to upload. Please select a file smaller than ${MAX_MB}MB.`,
                    });
                  } else {
                    setFiles(newFile);
                    handleUploadFile(newFile);
                  }
                }
              }
            }}
          >
            <Button className="outline-none" isDisabled={isBlocked}>
              <AttachmentIcon
                className={
                  uploadAttachmentsStatus === "pending" || isBlocked
                    ? "text-gray-200"
                    : "text-black"
                }
              />
            </Button>
          </FileTrigger>
        )}
        <Button
          className="outline-none"
          onPress={handleSendPress}
          isDisabled={uploadAttachmentsStatus === "pending" || isBlocked}
        >
          <SendIcon
            className={
              uploadAttachmentsStatus === "pending" || isBlocked
                ? "text-gray-200"
                : "text-black"
            }
          />
        </Button>
      </div>
    </div>
  );
}
